/**
 * PATH FX (B4): ticker tweens on LightLayer path state, one channel at a time.
 *
 *   const fx = new PathFx(() => ctrl.light());
 *   await fx.tween("phoenix-marketcity", "head", L, Td, EASE.drift, { delay: 0.4 * Tc, signal });
 *
 * - A path's numeric state (head, tail, alpha, glow, width, pulse) is split into channels. Starting a tween
 *   (or a set()) on a channel cancels the tween already running on it (its promise resolves false), so two
 *   sequences never fight over one value and nothing leaks.
 * - Every value is a pure function of the ticker clock (delay counts from the call).
 * - Owners: a sequence claims a path (claim → token) and later checks owns(id, token) before resetting it,
 *   so a late exit never resets a path that another sequence has taken over.
 */
import type { LightLayerAPI, RouteVisualState } from "@/contracts/map";
import { tween } from "@/lib/ticker";

export type Channel = "head" | "tail" | "alpha" | "glow" | "width" | "pulse";
const CHANNELS: Channel[] = ["head", "tail", "alpha", "glow", "width", "pulse"];
export type EaseFn = (t: number) => number;

export interface TweenOpts {
  from?: number;
  delay?: number;
  signal?: AbortSignal;
  idle?: boolean;
  /** Extra state written with every frame (e.g. comet on). */
  also?: Partial<RouteVisualState>;
  onFrame?: (v: number, eased: number, t: number) => void;
}

export class PathFx {
  private readonly runs = new Map<string, AbortController>();
  private readonly owners = new Map<string, symbol>();

  constructor(private readonly light: () => LightLayerAPI | null) {}

  private key(id: string, ch: Channel): string {
    return `${id}\u0000${ch}`;
  }

  get(id: string): RouteVisualState | undefined {
    try {
      return this.light()?.getState(id);
    } catch {
      return undefined;
    }
  }

  private write(id: string, patch: Partial<RouteVisualState>): void {
    try {
      this.light()?.setState(id, patch);
    } catch {
      /* the map is going away */
    }
  }

  /** A new owner token for the path (the previous owner loses it). */
  claim(id: string): symbol {
    const t = Symbol(id);
    this.owners.set(id, t);
    return t;
  }
  owns(id: string, token: symbol): boolean {
    return this.owners.get(id) === token;
  }
  release(id: string, token: symbol): void {
    if (this.owners.get(id) === token) this.owners.delete(id);
  }

  /** Stops the tween on one channel (or every channel of the path). */
  cancel(id: string, ch?: Channel): void {
    for (const c of ch ? [ch] : CHANNELS) {
      const k = this.key(id, c);
      const ac = this.runs.get(k);
      if (ac) {
        this.runs.delete(k);
        ac.abort();
      }
    }
  }

  /** Writes state now; every channel in the patch stops tweening. */
  set(id: string, patch: Partial<RouteVisualState>): void {
    for (const c of CHANNELS) if (c in patch) this.cancel(id, c);
    this.write(id, patch);
  }

  isRunning(id: string, ch: Channel): boolean {
    return this.runs.has(this.key(id, ch));
  }

  /** Tweens one channel to `to`. Resolves true when it arrived, false when cancelled or superseded. */
  tween(id: string, ch: Channel, to: number, duration: number, ease: EaseFn, o: TweenOpts = {}): Promise<boolean> {
    this.cancel(id, ch);
    if (o.signal?.aborted) return Promise.resolve(false);
    const k = this.key(id, ch);
    const ac = new AbortController();
    this.runs.set(k, ac);
    const onOuter = () => ac.abort();
    o.signal?.addEventListener("abort", onOuter, { once: true });
    let from: number | null = o.from ?? null;
    const done = (ok: boolean) => {
      o.signal?.removeEventListener("abort", onOuter);
      if (this.runs.get(k) === ac) this.runs.delete(k);
      return ok && !ac.signal.aborted;
    };
    return tween(
      Math.max(0, duration),
      ease,
      (e, t) => {
        if (from === null) {
          const cur = this.get(id)?.[ch];
          from = typeof cur === "number" ? cur : 0;
        }
        const v = from + (to - from) * e;
        this.write(id, o.also ? { ...o.also, [ch]: v } : { [ch]: v });
        o.onFrame?.(v, e, t);
      },
      { signal: ac.signal, delay: o.delay, idle: o.idle },
    ).then(done);
  }

  /** Stops every tween (destroy / cancel). */
  stopAll(): void {
    for (const ac of this.runs.values()) ac.abort();
    this.runs.clear();
  }
}
